/**
 * Product Media Pagination
 * Adds pagination dots to product image gallery (mobile)
 */

class ProductMediaPagination {
  constructor(container) {
    this.container = container;
    this.gallery = container.querySelector('slider-component');
    this.slider = container.querySelector('.product__media-list');
    this.slides = container.querySelectorAll('.product__media-item');
    this.paginationContainer = null;
    
    if (!this.gallery || !this.slider || this.slides.length === 0) return;
    
    this.init();
  }

  init() {
    this.createPagination();
    this.bindEvents();
    this.updateActiveDot();
  }

  createPagination() {
    // Only show pagination on mobile (handled by CSS as well)
    this.paginationContainer = document.createElement('div');
    this.paginationContainer.classList.add('product-media-pagination');
    
    this.slides.forEach((slide, index) => {
      const dot = document.createElement('button');
      dot.classList.add('product-media-pagination__dot');
      dot.setAttribute('aria-label', `Go to image ${index + 1}`);
      dot.setAttribute('data-index', index);
      
      if (index === 0) {
        dot.classList.add('active');
      }
      
      dot.addEventListener('click', () => {
        this.scrollToSlide(index);
      });
      
      this.paginationContainer.appendChild(dot);
    });
    
    // Insert pagination after the slider
    this.gallery.appendChild(this.paginationContainer);
  }

  bindEvents() {
    // Listen for scroll events on the slider
    if (this.slider) {
      this.slider.addEventListener('scroll', this.debounce(() => {
        this.updateActiveDot();
      }, 100));
    }

    // Listen for variant changes that might change featured image
    document.addEventListener('variant:change', () => {
      setTimeout(() => this.updateActiveDot(), 100);
    });
  }

  scrollToSlide(index) {
    const slide = this.slides[index];
    if (!slide) return;
    
    // Scroll the slide into view
    slide.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'start'
    });
    
    // Update active state immediately for better UX
    this.setActiveDot(index);
  }

  updateActiveDot() {
    if (!this.slider || !this.paginationContainer) return;
    
    // Find which slide is most visible
    const sliderRect = this.slider.getBoundingClientRect();
    const sliderCenter = sliderRect.left + (sliderRect.width / 2);
    
    let closestIndex = 0;
    let closestDistance = Infinity;
    
    this.slides.forEach((slide, index) => {
      const slideRect = slide.getBoundingClientRect();
      const slideCenter = slideRect.left + (slideRect.width / 2);
      const distance = Math.abs(slideCenter - sliderCenter);
      
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });
    
    this.setActiveDot(closestIndex);
  }

  setActiveDot(index) {
    if (!this.paginationContainer) return;
    
    const dots = this.paginationContainer.querySelectorAll('.product-media-pagination__dot');
    dots.forEach((dot, i) => {
      if (i === index) {
        dot.classList.add('active');
      } else {
        dot.classList.remove('active');
      }
    });
  }

  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }
}

// Initialize pagination when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initProductMediaPagination);
} else {
  initProductMediaPagination();
}

function initProductMediaPagination() {
  const mediaGalleries = document.querySelectorAll('media-gallery');
  mediaGalleries.forEach(gallery => {
    new ProductMediaPagination(gallery);
  });
}

// Re-initialize on section load (for theme editor)
document.addEventListener('shopify:section:load', function(event) {
  const mediaGallery = event.target.querySelector('media-gallery');
  if (mediaGallery) {
    new ProductMediaPagination(mediaGallery);
  }
});

